import { useEffect, useState } from 'react'
import { useMenuEngineering } from '../hooks/useMenuEngineering.js'
import { useMenuEngineeringBrief } from '../hooks/useMenuEngineeringBrief.js'
import { formatMoney } from '../lib/format.js'
import { AGENT_LABELS } from '../lib/agents.js'
import KitchenLoader from './KitchenLoader.jsx'
import MenuEngineeringScatter from './MenuEngineeringScatter.jsx'

// Menu engineering card — the classic Don Smith 2x2 matrix.
// Sits on the Insights screen. Restaurants typically pay a consultant
// to draw this on a napkin once a quarter; we generate it from the
// data the manager already has every time the tab loads.
//
// Quadrant logic lives in vw_menu_engineering (SQL). This component
// just renders the four buckets with the top items in each, plus a
// short managerial action under each label so the manager doesn't
// have to remember what "Plowhorse" means in textbook terms.

const QUADRANTS = [
  {
    id: 'star',
    label: 'Stars',
    icon: '★',
    short: 'Feature these.',
    desc: 'Popular AND profitable — these are your hits.',
    tone: 'emerald',
  },
  {
    id: 'plowhorse',
    label: 'Plowhorses',
    icon: '◆',
    short: 'Raise price or cut cost.',
    desc: 'Customers love them but margin is thin.',
    tone: 'amber',
  },
  {
    id: 'puzzle',
    label: 'Puzzles',
    icon: '◇',
    short: 'Promote or reposition.',
    desc: 'Profitable but not selling — server training, menu placement, photo.',
    tone: 'blue',
  },
  {
    id: 'dog',
    label: 'Dogs',
    icon: '○',
    short: 'Cut them.',
    desc: 'Slow AND unprofitable — kill them or rework completely.',
    tone: 'red',
  },
]

// Per-tone color classes, kept inline so Tailwind's JIT picks them up.
const TONES = {
  emerald: {
    border: 'border-emerald-200',
    bg:     'bg-emerald-50',
    title:  'text-emerald-900',
    sub:    'text-emerald-800/80',
    chip:   'bg-emerald-100 text-emerald-900',
  },
  amber: {
    border: 'border-amber-200',
    bg:     'bg-amber-50',
    title:  'text-amber-900',
    sub:    'text-amber-800/80',
    chip:   'bg-amber-100 text-amber-900',
  },
  blue: {
    border: 'border-blue-200',
    bg:     'bg-blue-50',
    title:  'text-blue-900',
    sub:    'text-blue-800/80',
    chip:   'bg-blue-100 text-blue-900',
  },
  red: {
    border: 'border-red-200',
    bg:     'bg-red-50',
    title:  'text-red-900',
    sub:    'text-red-800/80',
    chip:   'bg-red-100 text-red-900',
  },
}

export default function MenuEngineeringCard({ onBriefGenerated } = {}) {
  const { rows, byClass, hasData, loading, error, thresholds, refresh } = useMenuEngineering()
  const { brief, phase, generate, error: briefError, tinyData } = useMenuEngineeringBrief()

  // All hooks above the early returns — CLAUDE.md house rule, PromotionsScreen #310 incident 2026-05-10. Do not move below.
  const [activeQuadrant, setActiveQuadrant] = useState(null)

  // Lift the parsed brief up to the parent (WeeklyInsightsScreen) so
  // the kill-candidates card can show per-item rationales as well.
  // M1 — fire on EVERY brief change, not just on phase==='ready'.
  // When generate() clears `brief` at the top of a regen attempt,
  // the parent's menuBrief should clear too — otherwise a failed
  // regen leaves stale rationales attached to the kill-candidates
  // modal pointing at items that may no longer be in the snapshot.
  useEffect(() => {
    if (!onBriefGenerated) return
    onBriefGenerated(brief)
  }, [brief, onBriefGenerated])

  // Defensive: if the active filter's bucket becomes empty (e.g. on a
  // restaurant switch without unmount), clear the filter so the scatter
  // doesn't render with everything dimmed to 30%.
  useEffect(() => {
    if (activeQuadrant && (byClass?.[activeQuadrant]?.length ?? 0) === 0) {
      setActiveQuadrant(null)
    }
  }, [byClass, activeQuadrant])

  if (loading) {
    return (
      <section className="mx-4 mt-4 rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-black/50">
          {AGENT_LABELS.strategistRead}
        </div>
        <div className="mt-2 text-sm text-black/50">Mapping the matrix…</div>
      </section>
    )
  }

  if (error) {
    return (
      <section className="mx-4 mt-4 rounded-2xl border border-red-200 bg-red-50/40 p-4 shadow-sm">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-red-900/70">
          {AGENT_LABELS.strategistRead}
        </div>
        <div className="mt-2 text-sm text-red-800">{error}</div>
        <button
          type="button"
          onClick={refresh}
          className="mt-2 rounded-md px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-100"
        >
          Retry
        </button>
      </section>
    )
  }

  if (!hasData) {
    return (
      <section className="mx-4 mt-4 rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-black/55">
          {AGENT_LABELS.strategistRead}
        </div>
        <p className="mt-2 text-sm text-black/65 leading-snug">
          Need menu items with both sales history AND a recipe to map the matrix.
          Add recipes for your menu items in{' '}
          <span className="font-semibold">Settings → Recipes</span> and import a
          longer sales history to populate this card.
        </p>
      </section>
    )
  }

  return (
    <section className="mx-4 mt-4 rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-black/55">
          {AGENT_LABELS.strategistRead}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={generate}
            disabled={phase === 'generating'}
            className="rounded-md px-2 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50"
          >
            {phase === 'ready' || phase === 'error' ? 'Re-run AI brief' : '✨ Generate AI brief'}
          </button>
          <button
            type="button"
            onClick={refresh}
            className="rounded-md px-2 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-50"
          >
            Refresh
          </button>
        </div>
      </div>
      <p className="mt-1 text-xs text-black/60 leading-snug">
        Last 90 days. Popularity (units sold) vs profit per item, restaurant-relative.
        Each quadrant has a clear next move.
      </p>

      {/* AI brief region */}
      {phase === 'generating' && (
        <div className="mt-3">
          <KitchenLoader
            align="row"
            size="sm"
            messages={[
              'Reading 90 days of menu mix…',
              'Mapping items onto the matrix…',
              'Writing the strategic read…',
            ]}
          />
        </div>
      )}

      {phase === 'error' && (
        <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          {briefError || 'AI could not generate the brief. Try again.'}
        </div>
      )}

      {phase === 'ready' && tinyData && (
        <div className="mt-3 text-sm italic text-black/60 leading-snug">
          {brief.headline}
        </div>
      )}

      {phase === 'ready' && !tinyData && brief && (
        <div className="mt-3">
          <div className="text-base font-semibold leading-snug text-black/90">
            {brief.headline}
          </div>
          {brief.bullets?.length > 0 && (
            <ul className="mt-2 space-y-1 text-sm leading-snug">
              {brief.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
                  <span className="text-black/80">{b}</span>
                </li>
              ))}
            </ul>
          )}
          {brief.quadrant_actions && (
            <div className="mt-3 grid grid-cols-2 gap-2">
              {QUADRANTS.map((q) => {
                const tone = TONES[q.tone] || TONES.blue
                const action = brief.quadrant_actions?.[q.id]
                if (!action) return null
                return (
                  <div
                    key={q.id}
                    className={`rounded-lg border px-2.5 py-1.5 ${tone.border} ${tone.bg}`}
                  >
                    <div className={`text-[10px] font-bold uppercase tracking-wide ${tone.title}`}>
                      {q.icon} {q.label}
                    </div>
                    <div className={`mt-0.5 text-[11px] leading-snug ${tone.sub}`}>
                      {action}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Scatter chart — visual companion to the 4-grid below */}
      <div className="mt-4">
        <MenuEngineeringScatter
          rows={rows}
          thresholds={thresholds}
          activeQuadrant={activeQuadrant}
        />
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        {QUADRANTS.map((q) => (
          <Quadrant
            key={q.id}
            quadrant={q}
            items={byClass[q.id] || []}
            active={activeQuadrant === q.id}
            onToggle={() =>
              setActiveQuadrant((cur) => (cur === q.id ? null : q.id))
            }
          />
        ))}
      </div>

      {thresholds && (thresholds.avg_units > 0 || thresholds.avg_cm > 0) && (
        <div className="mt-3 text-[11px] text-black/50 leading-snug">
          Thresholds for your restaurant: above-average popularity is{' '}
          <span className="font-semibold tabular-nums">
            {Math.round(thresholds.avg_units)}+ units / 90d
          </span>
          , above-average profit is{' '}
          <span className="font-semibold tabular-nums">
            {formatMoney(thresholds.avg_cm)}/unit
          </span>{' '}
          contribution margin.
        </div>
      )}
    </section>
  )
}

function Quadrant({ quadrant, items, active, onToggle }) {
  const tone = TONES[quadrant.tone] || TONES.blue
  const activeRing = active ? ' ring-2 ring-offset-2 ring-amber-500' : ''
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      className={`block w-full rounded-xl border p-3 text-left transition focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 ${tone.border} ${tone.bg}${activeRing}`}
    >
      <div className="flex items-baseline justify-between gap-1">
        <div className={`text-xs font-bold ${tone.title}`}>
          <span aria-hidden="true">{quadrant.icon}</span> {quadrant.label}
        </div>
        <div className={`tabular-nums text-[10px] font-semibold ${tone.title} opacity-65`}>
          {items.length}
        </div>
      </div>
      <p className={`mt-0.5 text-[10px] font-semibold leading-snug ${tone.sub}`}>
        {quadrant.short}
      </p>

      {items.length === 0 ? (
        <div className={`mt-2 text-[11px] italic opacity-65 ${tone.title}`}>
          (none)
        </div>
      ) : (
        <ul className="mt-2 space-y-0.5">
          {items.slice(0, 5).map((it) => (
            <li
              key={it.item_id}
              className={`truncate text-[11px] ${tone.title}`}
              title={`${it.item_name} · ${Math.round(it.units_90d)}u · ${formatMoney(it.cm_per_unit)} CM/unit`}
            >
              <span className="font-medium">{it.item_name}</span>
              <span className="opacity-60"> · {Math.round(it.units_90d)}u</span>
            </li>
          ))}
          {items.length > 5 && (
            <li className={`text-[11px] italic opacity-60 ${tone.title}`}>
              +{items.length - 5} more
            </li>
          )}
        </ul>
      )}
    </button>
  )
}
